import {generatePath, useNavigate} from "react-router-dom";
import {useCallback, useMemo} from "react";
import {urls} from "~/routes/consumerUrls";
import NiceModal from "@ebay/nice-modal-react";
import GenerateContributionLinkDialog from "~/pages/account/payment/guest-payment/GenerateContributionLinkDialog";
import {useConsumerMenu} from "../../context/MenuContext";
import {useGetAccount} from "~/api/requests";
import {useAccountId} from "~/hooks";

export const usePrePlanOptions = () => {
  const {trackAndCloseMenu} = useConsumerMenu();
  const accountId = useAccountId();

  const {data: account} = useGetAccount({accountId}, {enabled: !!accountId});
  const navigate = useNavigate();

  const hasActivePaymentPlan = useMemo(() => {
    if (account) return account.active_payment_plan;
  }, [account]);

  const isLegalCollection = useMemo(() => {
    return account?.status === "legal_collection";
  }, [account]);

  const totalBalance = hasActivePaymentPlan ? account?.active_payment_plan?.total_amount : account?.total_balance;
  const shouldDisplayPrePlanOptions = accountId && !hasActivePaymentPlan && !isLegalCollection;

  const isDisputeButtonDisabled = useMemo<boolean | undefined>(() => {
    return account?.active_dispute || false;
  }, [account]);

  const onContributionLinkClick = useCallback(async (): Promise<void> => {
    trackAndCloseMenu("share_contribution_link");
    await NiceModal.show(GenerateContributionLinkDialog, {
      totalBalance,
      accountId,
    });
  }, [trackAndCloseMenu]);

  const onDisputeClick = useCallback((): void => {
    trackAndCloseMenu("file_dispute");
    navigate(generatePath(urls.dispute.url, {accountId}));
  }, [trackAndCloseMenu]);

  const onVerificationRequestClick = useCallback((): void => {
    trackAndCloseMenu("file_verification_request");
    navigate(generatePath(urls.verificationRequest.url, {accountId}));
  }, [trackAndCloseMenu]);

  return {
    onContributionLinkClick,
    onDisputeClick,
    onVerificationRequestClick,
    isDisputeButtonDisabled,
    shouldDisplayPrePlanOptions,
  };
};
